export interface LatLng {
  lat: number;
  lng: number;
}

/** Mean Earth radius in kilometres (IUGG). */
export const EARTH_RADIUS_KM = 6371.0088;

const toRad = (deg: number) => (deg * Math.PI) / 180;
const toDeg = (rad: number) => (rad * 180) / Math.PI;

/** Wraps a longitude into [-180, 180). */
export function normalizeLng(lng: number): number {
  return ((((lng + 180) % 360) + 360) % 360) - 180;
}

/** Normalizes an angle into [0, 360). */
export function normalizeDegrees(deg: number): number {
  return ((deg % 360) + 360) % 360;
}

/** Great-circle distance between two points (haversine formula), in kilometres. */
export function haversineKm(a: LatLng, b: LatLng): number {
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_KM * Math.asin(Math.min(1, Math.sqrt(h)));
}

/** Initial bearing from `a` to `b`, in degrees clockwise from north. */
export function bearingDeg(a: LatLng, b: LatLng): number {
  const φ1 = toRad(a.lat);
  const φ2 = toRad(b.lat);
  const Δλ = toRad(b.lng - a.lng);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  return normalizeDegrees(toDeg(Math.atan2(y, x)));
}

/** Point reached by travelling `distanceKm` from `origin` along `bearing` (degrees). */
export function destinationPoint(origin: LatLng, bearing: number, distanceKm: number): LatLng {
  const δ = distanceKm / EARTH_RADIUS_KM;
  const θ = toRad(bearing);
  const φ1 = toRad(origin.lat);
  const λ1 = toRad(origin.lng);
  const φ2 = Math.asin(Math.sin(φ1) * Math.cos(δ) + Math.cos(φ1) * Math.sin(δ) * Math.cos(θ));
  const λ2 = λ1 + Math.atan2(Math.sin(θ) * Math.sin(δ) * Math.cos(φ1), Math.cos(δ) - Math.sin(φ1) * Math.sin(φ2));
  return { lat: toDeg(φ2), lng: normalizeLng(toDeg(λ2)) };
}

/** Uniformly distributed random point inside a disc of radius `maxKm` around `origin`. */
export function randomPointNear(origin: LatLng, maxKm: number, rng: () => number = Math.random): LatLng {
  const distance = maxKm * Math.sqrt(rng());
  return destinationPoint(origin, rng() * 360, distance);
}

/** Axis-aligned bounding box of half-size `halfKm` around `center`, as [minLng, minLat, maxLng, maxLat]. */
export function bboxAround(center: LatLng, halfKm: number): [number, number, number, number] {
  const dLat = toDeg(halfKm / EARTH_RADIUS_KM);
  const cosLat = Math.max(0.05, Math.cos(toRad(center.lat)));
  const dLng = Math.min(180, dLat / cosLat);
  const round = (n: number) => Math.round(n * 1e6) / 1e6;
  return [
    round(Math.max(-180, center.lng - dLng)),
    round(Math.max(-90, center.lat - dLat)),
    round(Math.min(180, center.lng + dLng)),
    round(Math.min(90, center.lat + dLat)),
  ];
}

/** Slippy-map tile (x, y) containing a point at zoom `z`. */
export function lngLatToTile(point: LatLng, z: number): { x: number; y: number; z: number } {
  const n = 2 ** z;
  const lat = Math.max(-85.0511, Math.min(85.0511, point.lat));
  const x = Math.floor(((normalizeLng(point.lng) + 180) / 360) * n);
  const latRad = toRad(lat);
  const y = Math.floor(((1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2) * n);
  return { x: Math.min(n - 1, Math.max(0, x)), y: Math.min(n - 1, Math.max(0, y)), z };
}

const numberFr = (digits: number) =>
  new Intl.NumberFormat('fr-FR', { maximumFractionDigits: digits, minimumFractionDigits: 0 });

/** Human readable distance in French, e.g. "850 m", "3,4 km", "1 254 km". */
export function formatDistance(km: number): string {
  if (!Number.isFinite(km)) return '—';
  if (km < 1) return `${numberFr(0).format(Math.round(km * 1000))} m`;
  if (km < 10) return `${numberFr(1).format(km)} km`;
  return `${numberFr(0).format(Math.round(km))} km`;
}

/** Coordinates formatted as "48,85837° N, 2,29448° E". */
export function formatCoordinates(p: LatLng): string {
  const f = numberFr(5);
  const lat = `${f.format(Math.abs(p.lat))}° ${p.lat >= 0 ? 'N' : 'S'}`;
  const lng = `${f.format(Math.abs(p.lng))}° ${p.lng >= 0 ? 'E' : 'O'}`;
  return `${lat}, ${lng}`;
}

export function isValidLatLng(p: unknown): p is LatLng {
  if (!p || typeof p !== 'object') return false;
  const { lat, lng } = p as LatLng;
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    Number.isFinite(lat) &&
    Number.isFinite(lng) &&
    Math.abs(lat) <= 90 &&
    Math.abs(lng) <= 180
  );
}
